import { Component } from '@theme/component';

/**
 * Autoplays `[data-cp-video]` cards muted while they are in view, with a play/pause toggle and a
 * sound toggle per card. Only one video plays with sound at a time. Used by the video reel and
 * the single feature video.
 * Videos render with `preload="none"` and start buffering about one screen before they scroll in.
 * Native controls remain until the component connects, so videos stay playable without JavaScript.
 * With reduced motion requested, videos wait for the shopper to press play.
 *
 * @extends Component
 */
class CpVideoReelComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Starts buffering videos shortly before they scroll into view. @type {IntersectionObserver | undefined} */
  #preloadObserver;

  /** Videos the shopper paused; autoplay leaves them alone. @type {WeakSet<HTMLVideoElement>} */
  #userPaused = new WeakSet();

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#enhance();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#preloadObserver?.disconnect();
  }

  /**
   * Toggles playback of the video in the clicked card.
   * @param {Event} event - Click event from a `.cp-video__toggle` button.
   */
  toggle(event) {
    const video = this.#videoFor(event);
    if (!video) return;

    if (video.paused) {
      this.#userPaused.delete(video);
      this.#play(video);
    } else {
      this.#userPaused.add(video);
      video.pause();
    }
  }

  /**
   * Turns sound on or off for the video in the clicked card and mutes the others.
   * @param {Event} event - Click event from a `.cp-video__sound` button.
   */
  toggleSound(event) {
    const video = this.#videoFor(event);
    if (!video) return;

    if (!video.muted) {
      video.muted = true;
      return;
    }

    for (const other of this.querySelectorAll('video')) {
      if (other !== video && other instanceof HTMLVideoElement) other.muted = true;
    }

    video.muted = false;
    this.#userPaused.delete(video);
    if (video.paused) this.#play(video);
  }

  /**
   * @param {Event} event
   * @returns {HTMLVideoElement | null}
   */
  #videoFor(event) {
    const { target } = event;
    if (!(target instanceof Element)) return null;

    const video = target.closest('[data-cp-video]')?.querySelector('video');
    return video instanceof HTMLVideoElement ? video : null;
  }

  /** @param {HTMLVideoElement} video */
  #play(video) {
    video.play().catch(() => {
      // Playback can be rejected (for example by data saver); restore native controls as a fallback.
      video.controls = true;
    });
  }

  #enhance() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    this.#preloadObserver?.disconnect();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting } of entries) {
          if (!(target instanceof HTMLVideoElement)) continue;

          if (!isIntersecting) {
            if (!target.paused) target.pause();
          } else if (target.paused && !this.#userPaused.has(target) && !this.#reducedMotion.matches) {
            this.#play(target);
          }
        }
      },
      { threshold: 0.5 }
    );

    this.#preloadObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const { target, isIntersecting } of entries) {
          if (!isIntersecting || !(target instanceof HTMLVideoElement)) continue;

          observer.unobserve(target);
          if (target.preload === 'none') target.preload = this.#reducedMotion.matches ? 'metadata' : 'auto';
        }
      },
      { rootMargin: '100% 0px' }
    );

    for (const card of this.querySelectorAll('[data-cp-video]')) {
      const video = card.querySelector('video');
      const toggle = card.querySelector('.cp-video__toggle');
      const sound = card.querySelector('.cp-video__sound');
      if (!(video instanceof HTMLVideoElement) || !(toggle instanceof HTMLButtonElement)) continue;

      video.controls = false;
      video.muted = true;
      video.loop = true;
      toggle.hidden = false;
      if (sound instanceof HTMLButtonElement) sound.hidden = false;

      const syncPlaying = () => {
        const playing = !video.paused;
        card.toggleAttribute('data-playing', playing);
        toggle.setAttribute('aria-pressed', String(playing));
      };

      const syncSound = () => {
        const soundOn = !video.muted;
        card.toggleAttribute('data-sound', soundOn);
        if (!(sound instanceof HTMLButtonElement)) return;

        sound.setAttribute('aria-pressed', String(soundOn));
        const label = soundOn ? sound.dataset.labelMute : sound.dataset.labelUnmute;
        if (label) sound.setAttribute('aria-label', label);
      };

      video.addEventListener('play', syncPlaying, { signal });
      video.addEventListener('pause', syncPlaying, { signal });
      video.addEventListener('volumechange', syncSound, { signal });
      syncPlaying();
      syncSound();

      this.#observer.observe(video);
      this.#preloadObserver.observe(video);
    }
  }
}

if (!customElements.get('cp-video-reel-component')) {
  customElements.define('cp-video-reel-component', CpVideoReelComponent);
}
